import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ServiceStatus, ServiceStatusChangeAction } from '@shared/runless-api'
import Layout from '../components/layout/Layout'
import { useAuth } from '../hooks/useAuth'
import {
  estimateCostSavings,
  fetchAwsConnectionServiceCostSavingHistory,
  fetchAwsConnectionClusters,
  fetchOverallSavings,
  forceAwsConnectionServiceStatusChange,
  listAwsConnections,
  refreshAwsConnectionInventory,
  updateAwsConnectionServiceCostSavingRules,
  verifyAwsConnection,
  type AwsConnectionClusterItem,
  type AwsConnectionServiceCostSavingHistoryItem,
  type AwsConnectionServiceRuleInput,
  type AwsConnectionItem
} from '../lib/api'
import './DashboardPage.css'

type InventoryState = {
  loading: boolean
  error: string | null
  clusters: AwsConnectionClusterItem[]
}

type OverallSavingsState = {
  loading: boolean
  error: string | null
  current: number | null
}

type RefreshOptions = {
  silent?: boolean
}

type RuleFormState = {
  enableCostSaving: boolean
  expectedTaskCount: number
  shutdownAt: string
  startupAt: string
}

const DEFAULT_SHUTDOWN_CRON = 'cron(0 18 ? * MON,TUE,WED,THU,FRI,SAT,SUN *)'
const DEFAULT_STARTUP_CRON = 'cron(0 8 ? * MON,TUE,WED,THU,FRI,SAT,SUN *)'
const AWS_CRON_REGEX = /^cron\(\s*\d+\s+\d+\s+\?\s+\*\s+[A-Z,\-*]+\s+\*\s*\)$/i
const RUNTIME_AUTO_REFRESH_INTERVAL_MS = 5000
const RUNTIME_AUTO_REFRESH_WINDOW_MS = 60000
const INVENTORY_REFRESH_INTERVAL_MS = 15 * 60 * 1000
const INVENTORY_STALE_THRESHOLD_MS = INVENTORY_REFRESH_INTERVAL_MS * 2

type InventoryRefreshStatus = 'fresh' | 'stale' | 'failed' | 'never-refreshed'

function DashboardPage() {
  const { t } = useTranslation()
  const { isAuthenticated, accessToken, loading: authLoading, login } = useAuth()
  const [searchParams] = useSearchParams()
  const [connections, setConnections] = useState<AwsConnectionItem[]>([])
  const [inventoryByConnectionId, setInventoryByConnectionId] = useState<Record<string, InventoryState>>({})
  const [overallSavingsState, setOverallSavingsState] = useState<OverallSavingsState>({
    loading: false,
    error: null,
    current: null
  })
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [fetchLoading, setFetchLoading] = useState(false)
  const [verifyingId, setVerifyingId] = useState<string | null>(null)

  const connectedAccountId = searchParams.get('connected')

  useEffect(() => {
    if (!isAuthenticated || !accessToken) return

    setFetchLoading(true)
    setFetchError(null)

    listAwsConnections(accessToken).then(result => {
      setFetchLoading(false)
      if (result.ok) {
        setConnections(result.data)
      } else {
        setFetchError(result.error)
      }
    })
  }, [isAuthenticated, accessToken])

  useEffect(() => {
    if (!isAuthenticated || !accessToken) return

    setOverallSavingsState(current => ({
      ...current,
      loading: true,
      error: null
    }))

    fetchOverallSavings(accessToken).then(result => {
      if (result.ok) {
        setOverallSavingsState({
          loading: false,
          error: null,
          current: result.data.current
        })
      } else {
        setOverallSavingsState(current => ({
          ...current,
          loading: false,
          error: result.error
        }))
      }
    })
  }, [isAuthenticated, accessToken])

  const handleVerify = async (awsConnectionId: string) => {
    if (!accessToken) return

    setVerifyingId(awsConnectionId)
    setFetchError(null)
    const result = await verifyAwsConnection(awsConnectionId, accessToken)
    setVerifyingId(null)

    if (result.ok) {
      setConnections(prev =>
        prev.map(connection => (connection.awsConnectionId === awsConnectionId ? result.data : connection))
      )
    } else {
      setFetchError(result.error)
    }
  }

  const handleLoadInventory = async (awsConnectionId: string, options?: RefreshOptions) => {
    if (!accessToken) return

    const shouldRefreshLiveInventory = !options?.silent && Boolean(inventoryByConnectionId[awsConnectionId])

    if (!options?.silent) {
      setInventoryByConnectionId(current => ({
        ...current,
        [awsConnectionId]: {
          loading: true,
          error: null,
          clusters: current[awsConnectionId]?.clusters ?? []
        }
      }))
    }

    const result = shouldRefreshLiveInventory
      ? await refreshAwsConnectionInventory(awsConnectionId, accessToken)
      : await fetchAwsConnectionClusters(awsConnectionId, accessToken)

    setInventoryByConnectionId(current => ({
      ...current,
      [awsConnectionId]: {
        loading: false,
        error: result.ok ? null : result.error,
        clusters: result.ok ? result.data : current[awsConnectionId]?.clusters ?? []
      }
    }))

    if (!options?.silent) {
      const connectionsResult = await listAwsConnections(accessToken)
      if (connectionsResult.ok) {
        setConnections(connectionsResult.data)
      }
    }
  }

  const handleRefreshOverallSavings = async (options?: RefreshOptions) => {
    if (!accessToken) return

    if (!options?.silent) {
      setOverallSavingsState(current => ({
        ...current,
        loading: true,
        error: null
      }))
    }

    const result = await fetchOverallSavings(accessToken)
    if (result.ok) {
      setOverallSavingsState({
        loading: false,
        error: null,
        current: result.data.current
      })
      return
    }

    setOverallSavingsState(current => ({
      ...current,
      loading: false,
      error: result.error
    }))
  }

  if (authLoading) {
    return (
      <Layout>
        <div className='dashboard'>
          <p className='dashboard-loading'>{t('dashboard.loading')}</p>
        </div>
      </Layout>
    )
  }

  if (!isAuthenticated) {
    return (
      <Layout>
        <div className='dashboard'>
          <div className='dashboard-unauthenticated'>
            <h1>{t('dashboard.title')}</h1>
            <p>{t('dashboard.signInPrompt')}</p>
            <button className='btn btn-primary' onClick={() => login('/dashboard')}>
              {t('dashboard.signIn')}
            </button>
          </div>
        </div>
      </Layout>
    )
  }

  if (fetchLoading) {
    return (
      <Layout>
        <div className='dashboard'>
          <p className='dashboard-loading'>{t('dashboard.loadingConnections')}</p>
        </div>
      </Layout>
    )
  }

  if (fetchError) {
    return (
      <Layout>
        <div className='dashboard'>
          <div className='dashboard-error'>
            <h1>{t('dashboard.error')}</h1>
            <p>{fetchError}</p>
          </div>
        </div>
      </Layout>
    )
  }

  if (connections.length === 0) {
    return (
      <Layout>
        <div className='dashboard'>
          <h1>{t('dashboard.title')}</h1>
          <p className='dashboard-empty'>{t('dashboard.emptyDescription')}</p>
          <div className='dashboard-actions'>
            <Link className='btn btn-primary' to='/onboarding/complete'>
              {t('dashboard.connectAccount')}
            </Link>
            <a href='https://aws.amazon.com/marketplace' target='_blank' rel='noopener noreferrer'>
              {t('dashboard.marketplaceLink')}
            </a>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className='dashboard'>
        <h1>{t('dashboard.title')}</h1>
        <p className='dashboard-subtitle'>{t('dashboard.subtitle')}</p>

        <section className='dashboard-summary-card'>
          <div className='dashboard-summary-header'>
            <div>
              <p className='dashboard-summary-label'>{t('dashboard.overallSavingsTitle')}</p>
              <p className='dashboard-summary-value'>
                {overallSavingsState.current !== null
                  ? formatCurrency(overallSavingsState.current)
                  : overallSavingsState.loading
                  ? t('dashboard.overallSavingsLoading')
                  : t('dashboard.overallSavingsUnavailable')}
              </p>
            </div>

            <button
              className='btn btn-secondary dashboard-summary-button'
              disabled={overallSavingsState.loading}
              onClick={() => void handleRefreshOverallSavings()}
            >
              {overallSavingsState.loading
                ? t('dashboard.overallSavingsRefreshing')
                : t('dashboard.overallSavingsRefresh')}
            </button>
          </div>

          <p className='dashboard-summary-hint'>{t('dashboard.overallSavingsHint')}</p>
          {overallSavingsState.error ? <p className='dashboard-summary-error'>{overallSavingsState.error}</p> : null}
        </section>

        {connectedAccountId ? (
          <div className='dashboard-banner'>{t('dashboard.connectedMessage', { accountId: connectedAccountId })}</div>
        ) : null}

        <div className='dashboard-actions'>
          <Link className='btn btn-primary' to='/onboarding/complete'>
            {t('dashboard.connectAnotherAccount')}
          </Link>
        </div>

        {connections.map(connection => (
          <article key={connection.awsConnectionId} className='dashboard-connection'>
            {(() => {
              const inventoryState = inventoryByConnectionId[connection.awsConnectionId]
              const canLoadInventory = connection.status === 'VERIFIED'
              const serviceCount = inventoryState ? getServiceCount(inventoryState.clusters) : 0
              const inventoryRefreshStatus = getInventoryRefreshStatus(connection)

              return (
                <>
                  <div className='dashboard-connection-header'>
                    <div>
                      <p className='dashboard-account-id'>
                        {t('dashboard.accountId', { accountId: connection.accountId })}
                      </p>
                      <p className='dashboard-connection-id'>
                        {t('dashboard.connectionId', { connectionId: connection.awsConnectionId })}
                      </p>
                    </div>
                    <div className='dashboard-badges'>
                      <StatusBadge value={connection.status} />
                      <StatusBadge value={connection.commercialStatus} kind='commercial' />
                      <StatusBadge
                        value={inventoryRefreshStatus}
                        label={getInventoryRefreshStatusLabel(inventoryRefreshStatus, t)}
                        kind='inventory'
                      />
                    </div>
                  </div>

                  <dl className='dashboard-details'>
                    <DetailRow label={t('dashboard.roleArn')} value={connection.roleArn} />
                    <DetailRow label={t('dashboard.installationId')} value={connection.installationId} />
                    <DetailRow
                      label={t('dashboard.stackId')}
                      value={connection.stackId ?? t('dashboard.notProvided')}
                    />
                    <DetailRow label={t('dashboard.createdAt')} value={formatDate(connection.createdAt)} />
                    <DetailRow label={t('dashboard.updatedAt')} value={formatDate(connection.updatedAt)} />
                    <DetailRow
                      label={t('dashboard.lastVerifiedAt')}
                      value={
                        connection.lastVerifiedAt
                          ? formatDate(connection.lastVerifiedAt)
                          : t('dashboard.notVerifiedYet')
                      }
                    />
                    <DetailRow
                      label={t('dashboard.lastInventoryRefreshAt')}
                      value={
                        connection.lastInventoryRefreshAt
                          ? formatDate(connection.lastInventoryRefreshAt)
                          : t('dashboard.notRefreshedYet')
                      }
                    />
                    <DetailRow
                      label={t('dashboard.lastInventoryRefreshAttemptAt')}
                      value={
                        connection.lastInventoryRefreshAttemptAt
                          ? formatDate(connection.lastInventoryRefreshAttemptAt)
                          : t('dashboard.notAttemptedYet')
                      }
                    />
                    {connection.lastInventoryRefreshFailedAt ? (
                      <DetailRow
                        label={t('dashboard.lastInventoryRefreshFailedAt')}
                        value={formatDate(connection.lastInventoryRefreshFailedAt)}
                      />
                    ) : null}
                    {connection.inventoryRefreshFailureCount > 0 ? (
                      <DetailRow
                        label={t('dashboard.inventoryRefreshFailureCount')}
                        value={connection.inventoryRefreshFailureCount.toString()}
                      />
                    ) : null}
                    {connection.lastInventoryRefreshError ? (
                      <DetailRow
                        label={t('dashboard.lastInventoryRefreshError')}
                        value={connection.lastInventoryRefreshError}
                      />
                    ) : null}
                  </dl>

                  <p className='dashboard-hint'>{getConnectionHint(connection, t)}</p>

                  <div className='dashboard-connection-actions'>
                    <button
                      className='btn btn-secondary dashboard-verify-btn'
                      disabled={verifyingId === connection.awsConnectionId}
                      onClick={() => void handleVerify(connection.awsConnectionId)}
                    >
                      {verifyingId === connection.awsConnectionId
                        ? t('dashboard.verifying')
                        : t('dashboard.verifyAgain')}
                    </button>

                    {canLoadInventory ? (
                      <button
                        className='btn btn-secondary dashboard-verify-btn'
                        disabled={inventoryState?.loading}
                        onClick={() => void handleLoadInventory(connection.awsConnectionId)}
                      >
                        {inventoryState?.loading
                          ? t('dashboard.loadingInventory')
                          : inventoryState
                          ? t('dashboard.refreshInventory')
                          : t('dashboard.loadInventory')}
                      </button>
                    ) : null}
                  </div>

                  {inventoryState ? (
                    <section className='dashboard-inventory'>
                      <div className='dashboard-inventory-header'>
                        <div>
                          <h2>{t('dashboard.inventoryTitle')}</h2>
                          <p>
                            {t('dashboard.inventorySummary', {
                              clusterCount: inventoryState.clusters.length,
                              serviceCount
                            })}
                          </p>
                        </div>
                        <StatusBadge value={connection.status} kind='service' />
                      </div>

                      {inventoryState.error ? (
                        <p className='dashboard-inventory-error'>
                          {t('dashboard.inventoryError')}: {inventoryState.error}
                        </p>
                      ) : null}

                      {!inventoryState.loading && !inventoryState.error && inventoryState.clusters.length === 0 ? (
                        <p className='dashboard-inventory-empty'>{t('dashboard.inventoryEmpty')}</p>
                      ) : null}

                      {inventoryState.clusters.map(cluster => (
                        <section key={cluster.cluster} className='dashboard-cluster'>
                          <div className='dashboard-cluster-header'>
                            <h3>{cluster.cluster}</h3>
                            <span>{t('dashboard.serviceCount', { count: cluster.services.length })}</span>
                          </div>

                          <div className='dashboard-service-list'>
                            {cluster.services.map(service => (
                              <article key={`${cluster.cluster}:${service.name}`} className='dashboard-service-card'>
                                <div className='dashboard-service-header'>
                                  <div>
                                    <h4>{service.name}</h4>
                                    <p>
                                      {service.launchType} · {service.operatingSystem} · {service.architecture}
                                    </p>
                                  </div>
                                  <StatusBadge value={service.actualStatus} kind='service' />
                                </div>

                                <dl className='dashboard-service-metrics'>
                                  <DetailRow
                                    label={t('dashboard.desiredTaskCount')}
                                    value={String(service.desiredTaskCount)}
                                  />
                                  <DetailRow label={t('dashboard.runningCount')} value={String(service.runningCount)} />
                                  <DetailRow label={t('dashboard.pendingCount')} value={String(service.pendingCount)} />
                                  <DetailRow
                                    label={t('dashboard.minTaskCount')}
                                    value={formatOptionalNumber(service.minTaskCount)}
                                  />
                                  <DetailRow
                                    label={t('dashboard.maxTaskCount')}
                                    value={formatOptionalNumber(service.maxTaskCount)}
                                  />
                                  <DetailRow
                                    label={t('dashboard.vCpuPerTask')}
                                    value={formatOptionalNumber(service.vCpuPerTask)}
                                  />
                                  <DetailRow
                                    label={t('dashboard.memoryPerTask')}
                                    value={formatOptionalNumber(service.memoryPerTask)}
                                  />
                                  <DetailRow
                                    label={t('dashboard.costPerTaskPerMinute')}
                                    value={formatUsd(service.costPerTaskPerMinute)}
                                  />
                                </dl>

                                {accessToken ? (
                                  <ServiceRuntimePanel
                                    accessToken={accessToken}
                                    awsConnectionId={connection.awsConnectionId}
                                    cluster={cluster.cluster}
                                    service={service}
                                    onInventoryRefresh={options =>
                                      handleLoadInventory(connection.awsConnectionId, options)
                                    }
                                    onOverallSavingsRefresh={handleRefreshOverallSavings}
                                  />
                                ) : null}

                                {accessToken ? (
                                  <ServiceRuleEditor
                                    accessToken={accessToken}
                                    awsConnectionId={connection.awsConnectionId}
                                    cluster={cluster.cluster}
                                    service={service}
                                    onSaved={() => handleLoadInventory(connection.awsConnectionId)}
                                  />
                                ) : null}
                              </article>
                            ))}
                          </div>
                        </section>
                      ))}
                    </section>
                  ) : null}
                </>
              )
            })()}
          </article>
        ))}
      </div>
    </Layout>
  )
}

function ServiceRuntimePanel({
  accessToken,
  awsConnectionId,
  cluster,
  service,
  onInventoryRefresh,
  onOverallSavingsRefresh
}: {
  accessToken: string
  awsConnectionId: string
  cluster: string
  service: AwsConnectionClusterItem['services'][number]
  onInventoryRefresh: (options?: RefreshOptions) => Promise<void>
  onOverallSavingsRefresh: (options?: RefreshOptions) => Promise<void>
}) {
  const { t } = useTranslation()
  const [historyItems, setHistoryItems] = useState<AwsConnectionServiceCostSavingHistoryItem[]>([])
  const [historyNextToken, setHistoryNextToken] = useState<string | null>(null)
  const [historyLoaded, setHistoryLoaded] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyError, setHistoryError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState<ServiceStatusChangeAction | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionMessage, setActionMessage] = useState<string | null>(null)
  const [autoRefreshUntil, setAutoRefreshUntil] = useState<number | null>(null)

  const serviceStatus = resolveServiceStatus(service)
  const startupExpectedTaskCount = resolveStartupExpectedTaskCount(service)
  const canStart = serviceStatus === ServiceStatus.STOPPED || serviceStatus === ServiceStatus.UNKNOWN
  const canStop = serviceStatus === ServiceStatus.RUNNING || serviceStatus === ServiceStatus.STARTING

  useEffect(() => {
    setHistoryItems([])
    setHistoryNextToken(null)
    setHistoryLoaded(false)
    setHistoryLoading(false)
    setHistoryError(null)
    setActionLoading(null)
    setActionError(null)
    setActionMessage(null)
    setAutoRefreshUntil(null)
  }, [awsConnectionId, cluster, service.name])

  useEffect(() => {
    if (autoRefreshUntil === null) {
      return
    }

    let isDisposed = false
    let refreshInFlight = false

    const intervalId = window.setInterval(() => {
      if (Date.now() >= autoRefreshUntil) {
        window.clearInterval(intervalId)
        if (!isDisposed) {
          setAutoRefreshUntil(null)
        }
        return
      }

      if (refreshInFlight) {
        return
      }

      refreshInFlight = true

      void (async () => {
        try {
          const [historyResult] = await Promise.all([
            fetchAwsConnectionServiceCostSavingHistory(
              {
                awsConnectionId,
                cluster,
                service: service.name,
                maxResults: 5
              },
              accessToken
            ),
            onInventoryRefresh({ silent: true }),
            onOverallSavingsRefresh({ silent: true })
          ])

          if (isDisposed) {
            return
          }

          if (!historyResult.ok) {
            setHistoryError(historyResult.error)
            return
          }

          setHistoryLoaded(true)
          setHistoryError(null)
          setHistoryItems(current => mergeHistoryItems(historyResult.data.items, current))
          setHistoryNextToken(historyResult.data.nextToken ?? null)
        } finally {
          refreshInFlight = false
        }
      })()
    }, RUNTIME_AUTO_REFRESH_INTERVAL_MS)

    return () => {
      isDisposed = true
      window.clearInterval(intervalId)
    }
  }, [
    accessToken,
    autoRefreshUntil,
    awsConnectionId,
    cluster,
    onInventoryRefresh,
    onOverallSavingsRefresh,
    service.name
  ])

  const handleLoadHistory = async ({
    nextToken,
    mergeWithExisting = false,
    silent = false,
    startAutoRefresh = nextToken === undefined
  }: {
    nextToken?: string | null
    mergeWithExisting?: boolean
    silent?: boolean
    startAutoRefresh?: boolean
  } = {}) => {
    if (!silent) {
      setHistoryLoading(true)
    }

    if (!nextToken) {
      setHistoryError(null)
    }

    const result = await fetchAwsConnectionServiceCostSavingHistory(
      {
        awsConnectionId,
        cluster,
        service: service.name,
        nextToken,
        maxResults: 5
      },
      accessToken
    )

    if (!silent) {
      setHistoryLoading(false)
    }

    setHistoryLoaded(true)

    if (!result.ok) {
      setHistoryError(result.error)
      return false
    }

    setHistoryItems(current =>
      nextToken
        ? [...current, ...result.data.items]
        : mergeWithExisting
        ? mergeHistoryItems(result.data.items, current)
        : result.data.items
    )
    setHistoryNextToken(result.data.nextToken ?? null)

    if (startAutoRefresh) {
      setAutoRefreshUntil(Date.now() + RUNTIME_AUTO_REFRESH_WINDOW_MS)
    }

    return true
  }

  const handleForceAction = async (action: ServiceStatusChangeAction) => {
    setActionLoading(action)
    setActionError(null)
    setActionMessage(null)

    const result = await forceAwsConnectionServiceStatusChange(
      {
        awsConnectionId,
        cluster,
        service: service.name,
        action,
        expectedTaskCount: action === ServiceStatusChangeAction.STARTUP ? startupExpectedTaskCount : 0
      },
      accessToken
    )

    setActionLoading(null)

    if (!result.ok) {
      setActionError(result.error)
      return
    }

    if (!result.data.success) {
      setActionError(result.data.details ?? t('dashboard.serviceActionFailed'))
      return
    }

    setActionMessage(
      result.data.details ??
        (action === ServiceStatusChangeAction.STARTUP
          ? t('dashboard.serviceStartQueued')
          : t('dashboard.serviceStopQueued'))
    )

    void onInventoryRefresh({ silent: true })
    void onOverallSavingsRefresh({ silent: true })
    void handleLoadHistory({ mergeWithExisting: true, silent: true, startAutoRefresh: true })
  }

  return (
    <section className='dashboard-runtime-panel'>
      <div className='dashboard-runtime-header'>
        <div>
          <h5>{t('dashboard.runtimeSectionTitle')}</h5>
          <p>{t('dashboard.runtimeSectionHint')}</p>
        </div>

        <StatusBadge value={serviceStatus} kind='service' />
      </div>

      <div className='dashboard-runtime-actions'>
        <button
          className='btn btn-secondary dashboard-runtime-button'
          disabled={!canStart || actionLoading !== null}
          onClick={() => void handleForceAction(ServiceStatusChangeAction.STARTUP)}
        >
          {actionLoading === ServiceStatusChangeAction.STARTUP
            ? t('dashboard.serviceStarting')
            : t('dashboard.serviceStart')}
        </button>
        <button
          className='btn btn-secondary dashboard-runtime-button'
          disabled={!canStop || actionLoading !== null}
          onClick={() => void handleForceAction(ServiceStatusChangeAction.SHUTDOWN)}
        >
          {actionLoading === ServiceStatusChangeAction.SHUTDOWN
            ? t('dashboard.serviceStopping')
            : t('dashboard.serviceStop')}
        </button>
        <button
          className='btn btn-secondary dashboard-runtime-button'
          disabled={historyLoading}
          onClick={() => void handleLoadHistory()}
        >
          {historyLoading
            ? t('dashboard.historyLoading')
            : historyLoaded
            ? t('dashboard.historyRefresh')
            : t('dashboard.historyLoad')}
        </button>
      </div>

      <p className='dashboard-runtime-hint'>
        {t('dashboard.runtimeExpectedTaskCountHint', { count: startupExpectedTaskCount })}
      </p>

      {actionMessage ? <p className='dashboard-runtime-success'>{actionMessage}</p> : null}
      {actionError ? <p className='dashboard-runtime-error'>{actionError}</p> : null}

      {historyLoaded ? (
        <div className='dashboard-history'>
          <div className='dashboard-history-header'>
            <h6>{t('dashboard.historyTitle')}</h6>
            {historyNextToken ? (
              <button
                className='btn btn-secondary dashboard-runtime-button'
                disabled={historyLoading}
                onClick={() => void handleLoadHistory({ nextToken: historyNextToken, startAutoRefresh: false })}
              >
                {historyLoading ? t('dashboard.historyLoadingMore') : t('dashboard.historyLoadMore')}
              </button>
            ) : null}
          </div>

          {historyError ? <p className='dashboard-runtime-error'>{historyError}</p> : null}

          {!historyError && historyItems.length === 0 ? (
            <p className='dashboard-history-empty'>{t('dashboard.historyEmpty')}</p>
          ) : null}

          {historyItems.length > 0 ? (
            <ul className='dashboard-history-list'>
              {historyItems.map((item, index) => (
                <li
                  key={`${item.shutdownAt ?? 'pending'}:${item.startupAt ?? 'open'}:${index}`}
                  className='dashboard-history-item'
                >
                  <dl className='dashboard-history-metrics'>
                    <DetailRow
                      label={t('dashboard.historyShutdown')}
                      value={formatHistoryMoment(item.shutdownAt, item.shutdownType, t)}
                    />
                    <DetailRow
                      label={t('dashboard.historyStartup')}
                      value={
                        item.startupAt
                          ? formatHistoryMoment(item.startupAt, item.startupType, t)
                          : t('dashboard.historyAwaitingStartup')
                      }
                    />
                    <DetailRow
                      label={t('dashboard.historySaved')}
                      value={formatOptionalCurrency(item.amountSaved, t('dashboard.historyNoSavingsYet'))}
                    />
                  </dl>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}

function ServiceRuleEditor({
  accessToken,
  awsConnectionId,
  cluster,
  service,
  onSaved
}: {
  accessToken: string
  awsConnectionId: string
  cluster: string
  service: AwsConnectionClusterItem['services'][number]
  onSaved: () => Promise<void>
}) {
  const { t } = useTranslation()
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)
  const [estimateLoading, setEstimateLoading] = useState(false)
  const [estimateError, setEstimateError] = useState<string | null>(null)
  const [estimatedMonthlySavings, setEstimatedMonthlySavings] = useState<number | null>(null)
  const [formState, setFormState] = useState<RuleFormState>(() => createInitialRuleFormState(service))

  const { minExpectedTaskCount, maxExpectedTaskCount } = getExpectedTaskCountBounds(service)
  const existingRule = service.costSavingDetails?.rules?.[0] ?? null

  useEffect(() => {
    setFormState(createInitialRuleFormState(service))
    setIsEditing(false)
    setSaveError(null)
    setSaveMessage(null)
    setEstimateError(null)
    setEstimatedMonthlySavings(service.costSavingDetails?.savings.estimatedMonthly ?? null)
  }, [service])

  useEffect(() => {
    if (!isEditing || service.costPerTaskPerMinute <= 0) {
      return
    }

    const validationError = validateRuleFormState(formState, minExpectedTaskCount, maxExpectedTaskCount, t)
    if (validationError) {
      setEstimateLoading(false)
      setEstimateError(null)
      setEstimatedMonthlySavings(null)
      return
    }

    let isCancelled = false
    setEstimateLoading(true)
    setEstimateError(null)

    const timeoutId = setTimeout(() => {
      void estimateCostSavings(
        {
          costPerTaskPerMinute: service.costPerTaskPerMinute,
          rule: {
            shutdownAt: formState.shutdownAt.trim(),
            startupAt: formState.startupAt.trim()
          }
        },
        accessToken
      ).then(result => {
        if (isCancelled) {
          return
        }

        setEstimateLoading(false)

        if (!result.ok) {
          setEstimatedMonthlySavings(null)
          setEstimateError(result.error)
          return
        }

        setEstimatedMonthlySavings(result.data.estimatedMonthly * formState.expectedTaskCount)
      })
    }, 400)

    return () => {
      isCancelled = true
      clearTimeout(timeoutId)
    }
  }, [accessToken, formState, isEditing, maxExpectedTaskCount, minExpectedTaskCount, service.costPerTaskPerMinute, t])

  const handleSave = async () => {
    const validationError = validateRuleFormState(formState, minExpectedTaskCount, maxExpectedTaskCount, t)
    if (validationError) {
      setSaveError(validationError)
      return
    }

    setIsSaving(true)
    setSaveError(null)
    setSaveMessage(null)

    const input: AwsConnectionServiceRuleInput[] = [
      {
        awsConnectionId,
        cluster,
        service: service.name,
        enableCostSaving: formState.enableCostSaving,
        expectedTaskCount: formState.expectedTaskCount,
        pricePerMinutePerTask: service.costPerTaskPerMinute,
        rules: [
          {
            shutdownAt: formState.shutdownAt.trim(),
            startupAt: formState.startupAt.trim()
          }
        ]
      }
    ]

    const result = await updateAwsConnectionServiceCostSavingRules(input, accessToken)
    if (!result.ok || !result.data) {
      setIsSaving(false)
      setSaveError(result.ok ? t('dashboard.ruleSaveFailed') : result.error)
      return
    }

    await onSaved()
    setIsSaving(false)
    setIsEditing(false)
    setSaveMessage(t('dashboard.ruleSaved'))
  }

  const handleClear = async () => {
    setIsSaving(true)
    setSaveError(null)
    setSaveMessage(null)

    const input: AwsConnectionServiceRuleInput[] = [
      {
        awsConnectionId,
        cluster,
        service: service.name,
        enableCostSaving: false,
        expectedTaskCount: getDefaultExpectedTaskCount(service),
        pricePerMinutePerTask: service.costPerTaskPerMinute,
        rules: []
      }
    ]

    const result = await updateAwsConnectionServiceCostSavingRules(input, accessToken)
    if (!result.ok || !result.data) {
      setIsSaving(false)
      setSaveError(result.ok ? t('dashboard.ruleClearFailed') : result.error)
      return
    }

    await onSaved()
    setIsSaving(false)
    setIsEditing(false)
    setSaveMessage(t('dashboard.ruleCleared'))
  }

  return (
    <section className='dashboard-rule-editor'>
      <div className='dashboard-rule-editor-header'>
        <div>
          <h5>{t('dashboard.ruleSectionTitle')}</h5>
          <p>{existingRule ? t('dashboard.ruleConfiguredHint') : t('dashboard.ruleNotConfiguredHint')}</p>
        </div>

        {!isEditing ? (
          <button className='btn btn-secondary dashboard-rule-button' onClick={() => setIsEditing(true)}>
            {existingRule ? t('dashboard.ruleEdit') : t('dashboard.ruleConfigure')}
          </button>
        ) : null}
      </div>

      {service.costSavingDetails ? (
        <dl className='dashboard-rule-summary'>
          <DetailRow
            label={t('dashboard.ruleEnabled')}
            value={
              service.costSavingDetails.enableCostSaving ? t('dashboard.ruleEnabledYes') : t('dashboard.ruleEnabledNo')
            }
          />
          <DetailRow
            label={t('dashboard.ruleExpectedTaskCount')}
            value={String(service.costSavingDetails.expectedTaskCount)}
          />
          <DetailRow
            label={t('dashboard.ruleStartupCron')}
            value={existingRule?.startupAt ?? t('dashboard.notProvided')}
          />
          <DetailRow
            label={t('dashboard.ruleShutdownCron')}
            value={existingRule?.shutdownAt ?? t('dashboard.notProvided')}
          />
          <DetailRow
            label={t('dashboard.ruleEstimatedMonthlySavings')}
            value={formatCurrency(service.costSavingDetails.savings.estimatedMonthly)}
          />
        </dl>
      ) : (
        <p className='dashboard-rule-empty'>{t('dashboard.ruleNotConfigured')}</p>
      )}

      {saveMessage ? <p className='dashboard-rule-success'>{saveMessage}</p> : null}
      {saveError ? <p className='dashboard-rule-error'>{saveError}</p> : null}

      {isEditing ? (
        <div className='dashboard-rule-form'>
          <label className='dashboard-rule-checkbox'>
            <input
              checked={formState.enableCostSaving}
              onChange={event =>
                setFormState(current => ({
                  ...current,
                  enableCostSaving: event.target.checked
                }))
              }
              type='checkbox'
            />
            <span>{t('dashboard.ruleEnableCostSaving')}</span>
          </label>

          <label>
            <span>{t('dashboard.ruleExpectedTaskCount')}</span>
            <input
              className='dashboard-rule-input'
              max={maxExpectedTaskCount}
              min={minExpectedTaskCount}
              onChange={event =>
                setFormState(current => ({
                  ...current,
                  expectedTaskCount: Number(event.target.value)
                }))
              }
              type='number'
              value={formState.expectedTaskCount}
            />
            <small>{t('dashboard.ruleTaskCountRange', { min: minExpectedTaskCount, max: maxExpectedTaskCount })}</small>
          </label>

          <label>
            <span>{t('dashboard.ruleStartupCron')}</span>
            <input
              className='dashboard-rule-input'
              onChange={event =>
                setFormState(current => ({
                  ...current,
                  startupAt: event.target.value
                }))
              }
              placeholder={DEFAULT_STARTUP_CRON}
              type='text'
              value={formState.startupAt}
            />
          </label>

          <label>
            <span>{t('dashboard.ruleShutdownCron')}</span>
            <input
              className='dashboard-rule-input'
              onChange={event =>
                setFormState(current => ({
                  ...current,
                  shutdownAt: event.target.value
                }))
              }
              placeholder={DEFAULT_SHUTDOWN_CRON}
              type='text'
              value={formState.shutdownAt}
            />
          </label>

          <div className='dashboard-rule-preview'>
            <span>{t('dashboard.ruleEstimatedMonthlySavings')}</span>
            <strong>
              {estimateLoading
                ? t('dashboard.ruleEstimating')
                : estimateError
                ? t('dashboard.rulePreviewUnavailable')
                : estimatedMonthlySavings !== null
                ? formatCurrency(estimatedMonthlySavings)
                : t('dashboard.ruleEstimatedMonthlySavingsHint')}
            </strong>
          </div>

          <div className='dashboard-rule-actions'>
            {existingRule ? (
              <button
                className='btn btn-secondary dashboard-rule-button'
                disabled={isSaving}
                onClick={() => void handleClear()}
              >
                {t('dashboard.ruleClear')}
              </button>
            ) : null}
            <button
              className='btn btn-secondary dashboard-rule-button'
              disabled={isSaving}
              onClick={() => setIsEditing(false)}
            >
              {t('dashboard.ruleCancel')}
            </button>
            <button
              className='btn btn-primary dashboard-rule-button'
              disabled={isSaving}
              onClick={() => void handleSave()}
            >
              {isSaving ? t('dashboard.ruleSaving') : t('dashboard.ruleSave')}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  )
}

function StatusBadge({
  value,
  label,
  kind = 'connection'
}: {
  value: string
  label?: string
  kind?: 'connection' | 'commercial' | 'service' | 'inventory'
}) {
  const normalizedValue = value.toLowerCase().replace(/_/g, '-')

  return (
    <span
      className={`dashboard-status-badge dashboard-status-badge--${kind} dashboard-status-badge--${normalizedValue}`}
    >
      {label ?? value}
    </span>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  )
}

function formatDate(value: string): string {
  return new Date(value).toLocaleString()
}

function parseTimestamp(value: string | null | undefined): number | null {
  if (!value) {
    return null
  }

  const timestamp = new Date(value).getTime()
  return Number.isNaN(timestamp) ? null : timestamp
}

function formatOptionalNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '—'
  }

  return value.toString()
}

function formatUsd(value: number): string {
  return `${new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 4,
    maximumFractionDigits: 6
  }).format(value)}/min`
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)
}

function mergeHistoryItems(
  recentItems: AwsConnectionServiceCostSavingHistoryItem[],
  existingItems: AwsConnectionServiceCostSavingHistoryItem[]
): AwsConnectionServiceCostSavingHistoryItem[] {
  const recentKeys = new Set(recentItems.map(createHistoryItemKey))

  return [...recentItems, ...existingItems.filter(item => !recentKeys.has(createHistoryItemKey(item)))]
}

function createHistoryItemKey(item: AwsConnectionServiceCostSavingHistoryItem): string {
  return [item.shutdownAt ?? '', item.shutdownType ?? '', item.startupAt ?? '', item.startupType ?? ''].join('|')
}

function formatOptionalCurrency(value: number | null | undefined, fallback: string): string {
  if (typeof value !== 'number') {
    return fallback
  }

  return formatCurrency(value)
}

function formatHistoryMoment(
  timestamp: string | null | undefined,
  type: string | null | undefined,
  t: ReturnType<typeof useTranslation>['t']
): string {
  if (!timestamp) {
    return t('dashboard.notProvided')
  }

  const normalizedType = type ? type.toLowerCase() : t('dashboard.notProvided').toLowerCase()
  return `${formatDate(timestamp)} · ${normalizedType}`
}

function getServiceCount(clusters: AwsConnectionClusterItem[]): number {
  return clusters.reduce((total, cluster) => total + cluster.services.length, 0)
}

function createInitialRuleFormState(service: AwsConnectionClusterItem['services'][number]): RuleFormState {
  const rule = service.costSavingDetails?.rules?.[0]

  return {
    enableCostSaving: service.costSavingDetails?.enableCostSaving ?? false,
    expectedTaskCount: service.costSavingDetails?.expectedTaskCount ?? getDefaultExpectedTaskCount(service),
    shutdownAt: rule?.shutdownAt ?? DEFAULT_SHUTDOWN_CRON,
    startupAt: rule?.startupAt ?? DEFAULT_STARTUP_CRON
  }
}

function getDefaultExpectedTaskCount(service: AwsConnectionClusterItem['services'][number]): number {
  if (service.costSavingDetails?.enableCostSaving && service.costSavingDetails.expectedTaskCount) {
    return service.costSavingDetails.expectedTaskCount
  }

  if (service.minTaskCount !== null && service.minTaskCount !== undefined) {
    return service.minTaskCount
  }

  return service.desiredTaskCount
}

function resolveStartupExpectedTaskCount(service: AwsConnectionClusterItem['services'][number]): number {
  return Math.max(1, getDefaultExpectedTaskCount(service))
}

function resolveServiceStatus(service: AwsConnectionClusterItem['services'][number]): ServiceStatus {
  if (service.costSavingDetails?.enableCostSaving && (service.costSavingDetails.rules?.length ?? 0) > 0) {
    return service.costSavingDetails.status
  }

  return service.actualStatus
}

function getExpectedTaskCountBounds(service: AwsConnectionClusterItem['services'][number]): {
  minExpectedTaskCount: number
  maxExpectedTaskCount: number
} {
  const fallback = getDefaultExpectedTaskCount(service)
  const minExpectedTaskCount = service.minTaskCount ?? fallback
  const maxExpectedTaskCount = Math.max(minExpectedTaskCount, service.maxTaskCount ?? fallback)

  return {
    minExpectedTaskCount,
    maxExpectedTaskCount
  }
}

function validateRuleFormState(
  formState: RuleFormState,
  minExpectedTaskCount: number,
  maxExpectedTaskCount: number,
  t: ReturnType<typeof useTranslation>['t']
): string | null {
  if (!AWS_CRON_REGEX.test(formState.shutdownAt.trim()) || !AWS_CRON_REGEX.test(formState.startupAt.trim())) {
    return t('dashboard.ruleInvalidCron')
  }

  if (formState.shutdownAt.trim() === formState.startupAt.trim()) {
    return t('dashboard.ruleDifferentTimes')
  }

  const startupMinutes = parseCronMinutes(formState.startupAt)
  const shutdownMinutes = parseCronMinutes(formState.shutdownAt)
  if (startupMinutes === null || shutdownMinutes === null || shutdownMinutes <= startupMinutes) {
    return t('dashboard.ruleShutdownAfterStartup')
  }

  if (
    !Number.isFinite(formState.expectedTaskCount) ||
    formState.expectedTaskCount < minExpectedTaskCount ||
    formState.expectedTaskCount > maxExpectedTaskCount
  ) {
    return t('dashboard.ruleTaskCountInvalid', { min: minExpectedTaskCount, max: maxExpectedTaskCount })
  }

  return null
}

function parseCronMinutes(cron: string): number | null {
  const match = cron.trim().match(/^cron\((\d+)\s+(\d+)\s+.*\)$/i)
  if (!match) {
    return null
  }

  const minutes = Number(match[1])
  const hours = Number(match[2])
  if (Number.isNaN(minutes) || Number.isNaN(hours)) {
    return null
  }

  return hours * 60 + minutes
}

function getInventoryRefreshStatus(connection: AwsConnectionItem): InventoryRefreshStatus {
  const lastRefreshAt = parseTimestamp(connection.lastInventoryRefreshAt)
  const lastFailureAt = parseTimestamp(connection.lastInventoryRefreshFailedAt)

  if (lastFailureAt !== null && (lastRefreshAt === null || lastFailureAt > lastRefreshAt)) {
    return 'failed'
  }

  if (lastRefreshAt === null) {
    return 'never-refreshed'
  }

  if (Date.now() - lastRefreshAt > INVENTORY_STALE_THRESHOLD_MS) {
    return 'stale'
  }

  return 'fresh'
}

function getInventoryRefreshStatusLabel(
  status: InventoryRefreshStatus,
  t: ReturnType<typeof useTranslation>['t']
): string {
  switch (status) {
    case 'fresh':
      return t('dashboard.inventoryRefreshFresh')
    case 'stale':
      return t('dashboard.inventoryRefreshStale')
    case 'failed':
      return t('dashboard.inventoryRefreshFailed')
    case 'never-refreshed':
      return t('dashboard.inventoryRefreshNeverRefreshed')
    default:
      return status
  }
}

function getConnectionHint(connection: AwsConnectionItem, t: ReturnType<typeof useTranslation>['t']): string {
  if (connection.status === 'FAILED') {
    return t('dashboard.failedHint')
  }

  if (connection.status === 'PENDING_VERIFICATION') {
    return t('dashboard.pendingHint')
  }

  if (connection.commercialStatus === 'PENDING_COMMERCIAL_ACCESS') {
    return t('dashboard.pendingCommercialHint')
  }

  if (connection.commercialStatus === 'INACTIVE') {
    return t('dashboard.inactiveCommercialHint')
  }

  const inventoryRefreshStatus = getInventoryRefreshStatus(connection)
  if (inventoryRefreshStatus === 'failed') {
    return t('dashboard.inventoryRefreshFailedHint', {
      count: connection.inventoryRefreshFailureCount,
      error: connection.lastInventoryRefreshError ?? t('dashboard.notProvided')
    })
  }

  if (inventoryRefreshStatus === 'stale') {
    return t('dashboard.inventoryRefreshStaleHint')
  }

  if (inventoryRefreshStatus === 'never-refreshed') {
    return t('dashboard.inventoryRefreshNeverHint')
  }

  return t('dashboard.verifiedHint')
}

export default DashboardPage
